import { z } from 'zod'
import {
  CurrencyAmountSchema,
  ServiceRequestSchema,
  ServiceResponseSchema,
} from '@/validators/service.validator' // ===============================================================

// ===============================================================
// GENERAL
// ===============================================================

export const DateTimestampSchema = z.object({
  dateTime: z.string(),
  formatted: z.string(),
})

// ===============================================================
// CONTEXT LIST
// ===============================================================

export const AvailabilityStatusSchema = z.enum([
  'DISABLED',
  'NONE',
  'PARTIAL',
  'FULL',
])

export const ConfigurationParameterSchema = z.object({
  name: z.string(),
  value: z.string(),
})

export const ContextSchema = z.object({
  name: z.string(),
  description: z.string(),
  brand: z.string(),
	theme: z.string(),
  available: z.boolean(),
  availabilityStatus: AvailabilityStatusSchema,
  configurationParameters: z.array(ConfigurationParameterSchema),
})

export const ContextListSchema = z.object({
  contexts: z.array(ContextSchema),
  requestId: z.number(),
  serviceStatus: z.object({
    code: z.number(),
  }),
  traceId: z.string(),
  serverVersion: z.string(),
})

export const ContextListRequestSchema = z.object({
  contextListRequest: z.object({
    clientAgentString: z.string(),
    requestId: z.number(),
  }),
})

export const ContextListResponseSchema = z.object({
  contextListResponse: ContextListSchema,
})

// ===============================================================
// COMPOSE
// ===============================================================

export const BetslipDiscriminatorSchema = z.enum(['POOL', 'FIXED_ODDS'])

export const LevelSchema = z.enum(['ERROR', 'WARNING'])

export const SuggestionDiscriminatorSchema = z.enum([
  'BET_TYPE',
  'BET_OPTION',
  'POOL_TYPE',
  'POOL_OPTION',
  'MEETING',
  'EVENT',
  'SELECTION',
])

export const BookSchema = z.object({
  id: z.number(),
  description: z.string(),
  bookieId: z.optional(z.string()),
})

export const OutcomeSchema = z.object({
  id: z.number(),
  description: z.string(),
  selectionNo: z.optional(z.number()),
  enabled: z.boolean(),
  bookieId: z.optional(z.string()),
})

export const SelectionSchema = z.object({
  outcome: OutcomeSchema,
  book: BookSchema,
})

export const EventSchema = z.object({
  id: z.number(),
  description: z.string(),
  startDateTime: DateTimestampSchema,
  bookieId: z.optional(z.string()),
})

export const CategorySchema = z.object({
  id: z.number(),
  description: z.string(),
  bookieId: z.optional(z.string()),
  categoryPaths: z.array(z.string()),
})

export const SelectionGroupSchema = z.object({
  category: CategorySchema,
  event: EventSchema,
  selections: z.array(SelectionSchema),
  position: z.optional(z.number()),
})

export const BetslipSchema = z.object({
  discriminator: BetslipDiscriminatorSchema,
  formatted: z.string(),
  stake: z.optional(CurrencyAmountSchema),
  selectionGroups: z.optional(z.array(SelectionGroupSchema)),
})

export const ParameterSchema = z.object({
  name: z.string(),
  value: z.optional(z.string()),
})

export const ValidationErrorSchema = z.object({
  level: LevelSchema,
  code: z.string(),
  parameters: z.optional(z.array(ParameterSchema)),
})

export const ResolutionSchema = z.object({
  betslip: BetslipSchema,
  valid: z.boolean(),
  errors: z.array(ValidationErrorSchema),
})

export const SuggestionSchema = z.object({
  discriminator: SuggestionDiscriminatorSchema,
  description: z.string(),
  suggested: z.string(),
  formatted: z.string(),
  query: z.string(),
  errors: z.array(ValidationErrorSchema),
  debugInfo: z.optional(z.string()),
})

export const ComposeRequestSchema = ServiceRequestSchema.extend({
  query: z.string(),
  cursorPosition: z.optional(z.number()),
  defaultCurrency: z.optional(z.string()),
  locale: z.optional(z.string()),
	scopes: z.optional(z.array(z.string())),
})

export const ComposeResponseSchema = ServiceResponseSchema.extend({
  suggestions: z.array(SuggestionSchema),
  resolutions: z.array(ResolutionSchema),
})

export const ComposeRequestApiSchema = z.object({
  composeRequest: ComposeRequestSchema,
})

export const ComposeResponseApiSchema = z.object({
  composeResponse: ComposeResponseSchema,
})

// ===============================================================
// CONTEXT
// ===============================================================

export const ContextGetSchema = z.object({
  name: z.string(),
  description: z.string(),
  brand: z.string(),
  theme: z.string(),
})

export const ContextGetRequestSchema = ServiceRequestSchema.extend({})

export const ContextGetResponseSchema = ServiceResponseSchema.extend({
  context: ContextGetSchema,
})

export const ContextGetRequestApiSchema = z.object({
  contextGetRequest: ContextGetRequestSchema,
})

export const ContextGetResponseApiSchema = z.object({
  contextGetResponse: ContextGetResponseSchema,
})

// ===============================================================
// BET BUILDER
// ===============================================================

const BaseRequestFieldsSchema = z.object({
  clientAgentString: z.string(),
  requestId: z.number(),
})

export const BBMarketConditionSchema = z.object({
  marketDimensionName: z.string(),
  value: z.string(),
})

export const BBMarketSelectorSchema = z.object({
  marketTypeName: z.string(),
  marketConditions: z.array(BBMarketConditionSchema),
})

export const BBLegMarketCriteriaSchema = z.object({
  marketSelectors: z.array(BBMarketSelectorSchema),
})

export const NodeSelectorSchema = z
		.object({
			type: z.string()
		})

export const EventSelectorSchema = z
  .object({
    bookieId: z.string(),
    eventId: z.number(),
  })
  .partial()
  .refine(
    (data) => data.bookieId || data.eventId,
    'Either bookieId or eventId should be provided.',
  )

export const BBLegSelectorSchema = z.object({
  nodeSelector: NodeSelectorSchema,
  eventSelector: EventSelectorSchema,
  legMarketCriteria: BBLegMarketCriteriaSchema,
})

export const BetBuilderCriteriaSchema = z.object({
  bbLegSelectors: z.array(BBLegSelectorSchema),
})

export const BBBetslipSchema = z.object({
  stake: CurrencyAmountSchema,
  type: z.string(),
  option: z.string(),
})

export const FeatureSchema = z.object({
  name: z.string(),
  value: z.string(),
})

export const BetBuilderBuildRequestSchema = z.object({
  betslip: z.any(),
  legSelectors: z.array(BBLegSelectorSchema),
  features: z.optional(z.array(FeatureSchema)),
})

export const BBBuildRequestSchema = z.object({
  bbBuildRequest: z
    .object({
      betslip: z.any(),
      legSelectors: z.array(BBLegSelectorSchema),
      features: z.optional(z.array(FeatureSchema)),
    })
    .merge(BaseRequestFieldsSchema),
})

export const SampleSchema = z.object({
  name: z.string(),
  query: z.string(),
})

export const ScopeSchema = z.object({
  id: z.string(),
  available: z.boolean(),
  type: z.string(),
  title: z.string(),
  name: z.string(),
})

export const InsightPropertySchema = z.object({
	name: z.string(),
	value: z.string(),
})

export const InsightNodeSchema = z.object({
	type: z.string(),
	name: z.string(),
	props: z.optional(z.array(InsightPropertySchema))
})

export const InsightSchema = InsightNodeSchema.extend({
	topic: z.string(),
	nodes: z.optional(z.array(InsightNodeSchema)),
	startPos: z.number(),
	endPos: z.number()
})

export const FeedbackRequestSchema = ServiceRequestSchema.extend({
  traceId: z.string(),
  rating: z.number(),
  comment: z.optional(z.string()),
  speakerId: z.optional(z.string()),
})

export const FeedbackResponseSchema = ServiceResponseSchema.extend({})

export const SampleRequestSchema = ServiceRequestSchema.extend({
  maxSamples: z.optional(z.number()),
	scopes: z.optional(z.array(z.string())),
})

export const SampleResponseSchema = ServiceResponseSchema.extend({
  samples: z.optional(z.array(SampleSchema)),
})

export const ScopeRequestSchema = ServiceRequestSchema.extend({})

export const ScopeResponseSchema = ServiceResponseSchema.extend({
  scopes: z.optional(z.array(ScopeSchema)),
})

export const InsightRequestSchema = ServiceRequestSchema.extend({
	query: z.string(),
	cursorPosition: z.optional(z.number()),
	locale: z.optional(z.string()),
})

export const InsightResponseSchema = ServiceResponseSchema.extend({
	insights: z.optional(z.array(InsightSchema)),
})

export const FeedbackRequestApiSchema = z.object({
  feedbackRequest: FeedbackRequestSchema,
})

export const FeedbackResponseApiSchema = z.object({
  feedbackResponse: FeedbackResponseSchema,
})

export const SampleRequestApiSchema = z.object({
  sampleRequest: SampleRequestSchema,
})

export const SampleResponseApiSchema = z.object({
  sampleResponse: SampleResponseSchema,
})

export const ScopeRequestApiSchema = z.object({
  scopeRequest: ScopeRequestSchema,
})

export const ScopeResponseApiSchema = z.object({
  scopeResponse: ScopeResponseSchema,
})

export const InsightRequestApiSchema = z.object({
	insightRequest: InsightRequestSchema,
})

export const InsightResponseApiSchema = z.object({
	insightResponse: InsightResponseSchema,
})

// ===============================================================
// SPEECH
// ===============================================================

export const MicModeSchema = z.enum(['TAP', 'HOLD'])

export const SpeechRecogniseStartRequestSchema = ServiceRequestSchema.extend({
  sampleRate: z.number(),
  locale: z.string(),
  speakerId: z.optional(z.string()),
  micMode: z.optional(MicModeSchema),
  appendToQuery: z.optional(z.string()),
  scopes: z.optional(z.array(z.string())),
})

export const SpeechRecogniseStartResponseSchema = ServiceResponseSchema.extend({
  speechId: z.number(),
})

export const SpeechRecogniseStopRequestSchema = ServiceRequestSchema.extend({
  speechId: z.number(),
})

export const SpeechRecogniseStopResponseSchema = ServiceResponseSchema.extend({
  speechId: z.number(),
})

export const SpeechRecogniseQueryPushSchema = z.object({
  speechId: z.number(),
  locale: z.string(),
  query: z.optional(z.string()),
  complete: z.boolean(),
  traceId: z.string(),
  composeResponse: ComposeResponseSchema,
})
